import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../axiosInstance';
import { LanguageContext } from '../LanguageContext';
import { useWorkflowSchema } from '../hooks/useWorkflowSchema';
import {
  getParticipantStatusBannerClass,
  getParticipantStatusPresentation,
} from '../utils/participantStatus';

const formatLabel = (label = '') => {
  if (!label) return '';
  return label
    .toString()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());
};

const formatPrimitive = (value, language) => {
  if (value === null || typeof value === 'undefined' || value === '') return language === 'fr' ? 'Non fourni' : 'Not provided';
  if (typeof value === 'boolean') return language === 'fr' ? (value ? 'Oui' : 'Non') : value ? 'Yes' : 'No';
  if (value instanceof Date) return value.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA');
  return value.toString();
};

const renderValue = (value, language, path = '') => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return <span>{language === 'fr' ? 'Non fourni' : 'Not provided'}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span>{language === 'fr' ? 'Aucune donnée' : 'No data'}</span>;
    return (
      <ul className="govuk-list govuk-list--bullet govuk-!-margin-bottom-1">
        {value.map((entry, index) => (
          <li key={`${path}-${index}`}>
            {typeof entry === 'object' ? renderValue(entry, language, `${path}-${index}`) : formatPrimitive(entry, language)}
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value || {});
    if (!entries.length) return <span>{language === 'fr' ? 'Aucune donnée' : 'No data'}</span>;
    return (
      <dl className="govuk-summary-list govuk-!-margin-bottom-2">
        {entries.map(([childKey, childValue]) => (
          <div className="govuk-summary-list__row" key={`${path}-${childKey}`}>
            <dt className="govuk-summary-list__key">{formatLabel(childKey)}</dt>
            <dd className="govuk-summary-list__value">{renderValue(childValue, language, `${path}-${childKey}`)}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return <span>{formatPrimitive(value, language)}</span>;
};

const translateText = (value, language) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const langKey = language === 'fr' ? 'fr' : 'en';
    return value[langKey] || value.en || value.fr || '';
  }
  return '';
};

const PATH_TOKEN_REGEX = /[^.[\]]+/g;
const extractRootKey = (storageKey = '') => {
  const tokens = storageKey.match(PATH_TOKEN_REGEX);
  return tokens && tokens.length ? tokens[0] : storageKey;
};

const getValueAtPath = (source, path) => {
  if (!source || !path) return undefined;
  if (!path.includes('.') && !path.includes('[')) {
    return source[path];
  }
  const tokens = path.match(PATH_TOKEN_REGEX);
  if (!tokens) return source[path];
  return tokens.reduce((acc, token) => {
    if (acc === null || typeof acc === 'undefined') return undefined;
    if (Array.isArray(acc)) {
      const index = Number(token);
      if (Number.isInteger(index)) {
        return acc[index];
      }
      return undefined;
    }
    return acc[token];
  }, source);
};

const buildSectionsFromSchema = (schema, language) => {
  if (!Array.isArray(schema)) return [];
  return schema
    .map((step, index) => {
      if (!step || typeof step !== 'object') return null;
      const title =
        translateText(step.title, language) ||
        formatLabel(step.stepId || `step-${index + 1}`);
      const fields = [];
      const seen = new Set();
      const addField = (storageKey, labelSource) => {
        if (!storageKey || seen.has(storageKey)) return;
        const label =
          translateText(labelSource, language) || formatLabel(storageKey);
        fields.push({ storageKey, label });
        seen.add(storageKey);
      };
      const walk = (components) => {
        if (!Array.isArray(components)) return;
        components.forEach((component) => {
          if (!component || typeof component !== 'object') return;
          const storageKey =
            component.storageKey ||
            component.name ||
            component.props?.storageKey ||
            component.props?.name;
          const labelSource =
            component.label ||
            component.legend ||
            component.title ||
            component.props?.label ||
            component.props?.legend;
          if (storageKey) {
            addField(storageKey, labelSource);
          }
          const childSets = [
            component.components,
            component.children,
            component.props?.components,
            component.props?.children,
          ];
          childSets.forEach((set) => {
            if (Array.isArray(set) && set.length) {
              walk(set);
            }
          });
          const options = component.options || component.props?.options;
          if (Array.isArray(options)) {
            options.forEach((option) => {
              if (!option) return;
              if (Array.isArray(option.children)) {
                walk(option.children);
              }
              if (
                option.conditional &&
                Array.isArray(option.conditional.children)
              ) {
                walk(option.conditional.children);
              }
            });
          }
        });
      };
      walk(Array.isArray(step.components) ? step.components : []);
      if (!fields.length) return null;
      return {
        id: step.stepId || step.id || `step-${index + 1}`,
        title,
        fields,
      };
    })
    .filter(Boolean);
};

function SubmissionDetails() {
  const { language } = useContext(LanguageContext);
  const { tracking_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submission, setSubmission] = useState(null);
  const { schema } = useWorkflowSchema();

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const resp = await api.get(`/api/submissions/by-reference`, { params: { ref: tracking_id } });
        if (mounted) setSubmission(resp.data);
      } catch (e) {
        if (mounted) setError(language === 'fr' ? 'Échec du chargement.' : 'Failed to load submission.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [tracking_id, language]);

  const fmt = (ts) => (ts ? new Date(ts).toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA') : '—');

  const schemaSections = useMemo(
    () => buildSectionsFromSchema(schema, language),
    [schema, language]
  );

  const orderedSections = useMemo(() => {
    const payload = submission?.payload;
    if (!payload || typeof payload !== 'object') return [];
    if (!schemaSections.length) {
      return [
        {
          id: 'fallback',
          title: language === 'fr' ? 'Réponses soumises' : 'Submitted answers',
          fields: Object.entries(payload).map(([key, value]) => ({
            storageKey: key,
            label: formatLabel(key),
            value,
          })),
        },
      ];
    }
    const matchedRootKeys = new Set();
    const sections = schemaSections
      .map((section) => {
        const fields = section.fields.map((field) => {
          const value = getValueAtPath(payload, field.storageKey);
          const rootKey = extractRootKey(field.storageKey);
          if (rootKey) matchedRootKeys.add(rootKey);
          return { ...field, value };
        });
        if (!fields.length) return null;
        return { ...section, fields };
      })
      .filter(Boolean);

    const extraEntries = Object.entries(payload).filter(
      ([key]) => !matchedRootKeys.has(key)
    );
    if (extraEntries.length) {
      sections.push({
        id: 'other',
        title: language === 'fr' ? 'Autres réponses' : 'Other answers',
        fields: extraEntries.map(([key, value]) => ({
          storageKey: key,
          label: formatLabel(key),
          value,
        })),
      });
    }
    return sections;
  }, [schemaSections, submission, language]);
  const statusPresentation = submission
    ? getParticipantStatusPresentation(submission.external_status || submission.status, language)
    : null;
  const statusBannerClassName = statusPresentation
    ? getParticipantStatusBannerClass(statusPresentation.key)
    : 'govuk-notification-banner app-dashboard-card';

  return (
    <div className="govuk-width-container govuk-!-margin-top-3">
      <main className="govuk-main-wrapper" role="main">
        <h1 className="govuk-heading-l">{language === 'fr' ? 'Détails de la demande soumise' : 'Submitted Application Details'}</h1>
        <p><Link to="/dashboard" className="govuk-link">{language === 'fr' ? 'Retour au tableau de bord' : 'Back to dashboard'}</Link></p>
        {loading && <p className="govuk-body">{language === 'fr' ? 'Chargement...' : 'Loading...'}</p>}
        {error && !loading && <p className="govuk-error-message">{error}</p>}
        {!loading && !error && submission && (
          <div>
            {statusPresentation && (
              <div
                className={`${statusBannerClassName} govuk-!-margin-bottom-5`}
                role="region"
                aria-labelledby="submission-status-title"
                data-module="govuk-notification-banner"
              >
                <div className="govuk-notification-banner__header">
                  <h2 className="govuk-notification-banner__title" id="submission-status-title">
                    {language === 'fr' ? 'État de la demande' : 'Application status'}
                  </h2>
                </div>
                <div className="govuk-notification-banner__content">
                  <p className="govuk-body govuk-!-font-weight-bold govuk-!-margin-bottom-3">
                    {statusPresentation.summary}
                    {statusPresentation.actionHref && statusPresentation.actionLabel && (
                      <>
                        {' '}
                        <Link to={statusPresentation.actionHref} className="govuk-link">
                          {statusPresentation.actionLabel}
                        </Link>
                      </>
                    )}
                  </p>
                  {statusPresentation.nextSteps && (
                    <p className="govuk-body govuk-!-margin-bottom-0">{statusPresentation.nextSteps}</p>
                  )}
                </div>
              </div>
            )}
            <dl className="govuk-summary-list">
              <div className="govuk-summary-list__row">
                <dt className="govuk-summary-list__key">{language === 'fr' ? 'ID de suivi' : 'Tracking ID'}</dt>
                <dd className="govuk-summary-list__value" style={{ fontFamily: 'monospace' }}>{submission.tracking_id}</dd>
              </div>
              <div className="govuk-summary-list__row">
                <dt className="govuk-summary-list__key">{language === 'fr' ? 'Statut' : 'Status'}</dt>
                <dd className="govuk-summary-list__value">{statusPresentation?.label || submission.status || '—'}</dd>
              </div>
              <div className="govuk-summary-list__row">
                <dt className="govuk-summary-list__key">{language === 'fr' ? 'Soumis le' : 'Submitted on'}</dt>
                <dd className="govuk-summary-list__value">{fmt(submission.created_at)}</dd>
              </div>
              <div className="govuk-summary-list__row">
                <dt className="govuk-summary-list__key">{language === 'fr' ? 'Dernière mise à jour' : 'Last updated'}</dt>
                <dd className="govuk-summary-list__value">{fmt(submission.last_updated)}</dd>
              </div>
            </dl>
            <details className="govuk-details govuk-!-margin-top-4">
              <summary className="govuk-details__summary">
                <span className="govuk-details__summary-text">
                  {language === 'fr' ? 'Voir les réponses de la demande ISET' : 'View submitted ISET application'}
                </span>
              </summary>
              <div className="govuk-details__text">
                {orderedSections.length ? (
                  orderedSections.map((section) => (
                    <section key={section.id} className="govuk-!-margin-bottom-6">
                      <h3 className="govuk-heading-m govuk-!-margin-bottom-2">{section.title}</h3>
                      <dl className="govuk-summary-list govuk-!-margin-bottom-2">
                        {section.fields.map((field) => (
                          <div className="govuk-summary-list__row" key={field.storageKey}>
                            <dt className="govuk-summary-list__key">{field.label}</dt>
                            <dd className="govuk-summary-list__value">
                              {renderValue(field.value, language, field.storageKey)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </section>
                  ))
                ) : (
                  <p className="govuk-body">{language === 'fr' ? 'Aucune réponse disponible.' : 'No responses available.'}</p>
                )}
              </div>
            </details>
          </div>
        )}
      </main>
    </div>
  );
}

export default SubmissionDetails;
